import { useState, useEffect } from 'react';
import { Search, Pencil, Copy, Check, UserPlus, Shield, ShieldOff, X, Save, KeyRound, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn, formatDate, getInitials } from '@/lib/utils';
import type { User, Role, InviteCode } from '@/types';
import { api } from '@/lib/api';
import { AgentAccessPanel } from '@/components/admin/AgentAccessPanel';

async function checkedMutation(request: Promise<{ success?: boolean; error?: string }>) {
  const response = await request;
  if (response.success === false || response.error) throw new Error(response.error || 'Änderung fehlgeschlagen.');
}

export function UsersAdminPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [invites, setInvites] = useState<InviteCode[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadErrors, setLoadErrors] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('all');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [inviteForm, setInviteForm] = useState({ email: '', role_id: '', expires_days: 7 });

  const loadUsers = () => {
    setLoading(true);
    setLoadErrors((previous) => ({ ...previous, users: '' }));
    api
      .get<{ success?: boolean; error?: string; data?: User[] }>('/users?limit=200')
      .then((res) => {
        if (res.success === false || !Array.isArray(res.data)) throw new Error(res.error || 'Benutzer konnten nicht geladen werden.');
        setUsers(res.data);
      })
      .catch((error) => {
        setLoadErrors((previous) => ({ ...previous, users: error instanceof Error ? error.message : 'Benutzer konnten nicht geladen werden.' }));
        setUsers([]);
      })
      .finally(() => setLoading(false));
  };

  const loadInvites = () => {
    setLoadErrors((previous) => ({ ...previous, invites: '' }));
    api.get<{ success?: boolean; error?: string; data?: InviteCode[] }>('/admin/invite-codes?limit=100')
      .then((res) => {
        if (res.success === false || !Array.isArray(res.data)) throw new Error(res.error || 'Einladungen konnten nicht geladen werden.');
        setInvites(res.data);
      })
      .catch((error) => {
        setInvites([]);
        setLoadErrors((previous) => ({ ...previous, invites: error instanceof Error ? error.message : 'Einladungen konnten nicht geladen werden.' }));
      });
  };

  const loadRoles = () => {
    setLoadErrors((previous) => ({ ...previous, roles: '' }));
    api.get<{ success?: boolean; error?: string; data?: Role[] }>('/admin/roles')
      .then((res) => {
        if (res.success === false || !Array.isArray(res.data)) throw new Error(res.error || 'Rollen konnten nicht geladen werden.');
        setRoles(res.data);
      })
      .catch((error) => {
        setRoles([]);
        setLoadErrors((previous) => ({ ...previous, roles: error instanceof Error ? error.message : 'Rollen konnten nicht geladen werden.' }));
      });
  };

  useEffect(() => {
    loadUsers();
    loadInvites();
    loadRoles();
  }, []);

  const filtered = users.filter((u) => {
    const matchSearch = !search || u.full_name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || u.roles?.some((r: any) => r.id === filterRole);
    const matchActive = filterActive === 'all' || (filterActive === 'active' && u.is_active) || (filterActive === 'inactive' && !u.is_active);
    return matchSearch && matchRole && matchActive;
  });

  const openEditUser = (u: User) => {
    setEditingUser(u);
    setEditRoles(u.roles?.map((r: any) => r.id) ?? []);
    setEditUserDialogOpen(true);
  };

  const saveUserRoles = async () => {
    if (!editingUser) return;
    try {
      const currentRoles = new Set((editingUser.roles ?? []).map((r: any) => r.id));
      const nextRoles = new Set(editRoles);
      const toAdd = Array.from(nextRoles).filter((id) => !currentRoles.has(id));
      const toRemove = Array.from(currentRoles).filter((id) => !nextRoles.has(id));
      await Promise.all([
        ...toAdd.map((roleId) => checkedMutation(api.post(`/users/${editingUser.id}/roles`, { role_id: roleId }))),
        ...toRemove.map((roleId) => checkedMutation(api.delete(`/users/${editingUser.id}/roles/${roleId}`))),
      ]);
      setEditUserDialogOpen(false);
      loadUsers();
    } catch (e) {
      setError((e as Error).message ?? 'Rollen konnten nicht gespeichert werden.');
    }
  };

  const toggleActive = async (u: User) => {
    try {
      if (u.is_active) {
        await checkedMutation(api.patch(`/users/${u.id}/deactivate`));
      } else {
        await checkedMutation(api.patch(`/users/${u.id}/activate`));
      }
      loadUsers();
    } catch (e) {
      setError((e as Error).message ?? 'Status konnte nicht geändert werden.');
    }
  };

  const createInvite = async () => {
    try {
      const expires = new Date();
      expires.setDate(expires.getDate() + inviteForm.expires_days);
      await checkedMutation(api.post('/admin/invite-codes', {
        email: inviteForm.email || undefined,
        role_id: inviteForm.role_id || undefined,
        expires_at: expires.toISOString(),
      }));
      setInviteDialogOpen(false);
      setInviteForm({ email: '', role_id: '', expires_days: 7 });
      loadInvites();
    } catch (e) {
      setError((e as Error).message ?? 'Einladung konnte nicht erstellt werden.');
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const providerLabel: Record<string, string> = { local: 'Lokal', microsoft: 'Microsoft', google: 'Google' };

  if (loading && users.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Benutzerverwaltung</h1>
        </div>
        <Button className="shrink-0" onClick={() => setInviteDialogOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Einladung erstellen
        </Button>
      </div>

      <AgentAccessPanel />

      {Object.values(loadErrors).some(Boolean) && (
        <div role="alert" className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {Object.entries(loadErrors).filter(([, message]) => message).map(([key, message]) => <p key={key}>{message}</p>)}
          <Button className="mt-2" variant="outline" size="sm" disabled={loading} onClick={() => { loadUsers(); loadInvites(); loadRoles(); }}>
            <RefreshCw size={14} /> Erneut laden
          </Button>
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {error}
        </div>
      )}

      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle>Benutzer</CardTitle>
          <div className="flex flex-wrap gap-3 pt-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <Input placeholder="Suchen (Name, E-Mail)…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Rolle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Rollen</SelectItem>
                {roles.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="inactive">Inaktiv</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-3 text-left font-medium text-muted">Benutzer</th>
                  <th className="pb-3 text-left font-medium text-muted">Rollen</th>
                  <th className="pb-3 text-left font-medium text-muted">Anbieter</th>
                  <th className="pb-3 text-left font-medium text-muted">MFA</th>
                  <th className="pb-3 text-left font-medium text-muted">Letzter Login</th>
                  <th className="pb-3 text-left font-medium text-muted">Status</th>
                  <th className="pb-3 text-right font-medium text-muted">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-muted">Keine Benutzer gefunden.</td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr key={u.id} className="border-b border-border">
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={u.avatar_url} />
                            <AvatarFallback>{getInitials(u.full_name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-foreground">{u.full_name}</p>
                            <p className="text-muted">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.roles?.map((r: any) => <Badge key={r.id} variant="secondary">{r.name}</Badge>) ?? '—'}
                        </div>
                      </td>
                      <td className="py-3 text-muted">{providerLabel[u.provider] ?? u.provider}</td>
                      <td className="py-3">{u.mfa_enabled ? <Badge variant="success">Aktiv</Badge> : <span className="text-muted">Aus</span>}</td>
                      <td className="py-3 text-muted">{u.last_login_at ? formatDate(u.last_login_at, 'short') : '—'}</td>
                      <td className="py-3">
                        {u.is_active ? <Badge variant="success">Aktiv</Badge> : <Badge variant="destructive">Inaktiv</Badge>}
                      </td>
                      <td className="py-3 text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditUser(u)} title="Rollen bearbeiten">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => toggleActive(u)} title={u.is_active ? 'Deaktivieren' : 'Aktivieren'}>
                          {u.is_active ? <ShieldOff className="h-4 w-4 text-warning" /> : <Shield className="h-4 w-4 text-success" />}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle>Einladungscodes</CardTitle>
          <p className="text-sm text-muted">Codes kopieren und an neue Benutzer senden.</p>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {invites.filter((i) => i.is_active).length === 0 ? (
              <li className="py-4 text-center text-muted">{loadErrors.invites ? 'Einladungen nicht verfügbar.' : 'Keine aktiven Einladungscodes.'}</li>
            ) : (
              invites.filter((i) => i.is_active).map((i) => (
                <li key={i.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div>
                    <code className="font-mono text-sm text-foreground">{i.code}</code>
                    {i.email && <span className="ml-2 text-muted text-sm">{i.email}</span>}
                    <span className="ml-2 text-xs text-muted">gültig bis {formatDate(i.expires_at, 'short')}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => copyCode(i.code)}>
                    {copiedCode === i.code ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </li>
              ))
            )}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Einladungscode erstellen</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>E-Mail (optional)</Label>
              <Input type="email" placeholder="empfaenger@beispiel.ch" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>Standard-Rolle</Label>
              <Select value={inviteForm.role_id} onValueChange={(v) => setInviteForm((f) => ({ ...f, role_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Rolle wählen" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Gültig (Tage)</Label>
              <Input type="number" min={1} value={inviteForm.expires_days} onChange={(e) => setInviteForm((f) => ({ ...f, expires_days: Number(e.target.value) || 7 }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button onClick={createInvite}>
              <KeyRound className="h-4 w-4" />
              Code erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rollen bearbeiten – {editingUser?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Rollen</Label>
              <div className="flex flex-wrap gap-2">
                {roles.map((r: any) => (
                  <label key={r.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editRoles.includes(r.id)}
                      onChange={(e) => setEditRoles((prev) => e.target.checked ? [...prev, r.id] : prev.filter((id) => id !== r.id))}
                      className="rounded border-border"
                    />
                    <span className="text-sm">{r.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button onClick={saveUserRoles}>
              <Save className="h-4 w-4" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
